import { datadogRum } from '@datadog/browser-rum';
import type {
  ChangeTrackingActionContext,
  ChangeTrackingHeaders,
  ChangeTrackingRequestConfig,
  ChangeTrackingResolvedSource,
  ChangeTrackingSource,
  ChangeTrackingSourceId,
} from '@typeDefinitions/changeTrackingTypes';

import { CHANGE_TRACKING_SOURCE_IDS } from './changeTrackingRegistry';

const CHANGE_TRACKING_HEADER = 'Atg-Change-Source-Ids';
const CHANGE_TRACKING_HEADER_VALUE_LIMIT = 4000;
const CHANGE_TRACKING_DATADOG_ACTION = 'change_tracking_source_ids';

const getChangeTrackingSources = (
  sources: readonly ChangeTrackingSource[],
): ChangeTrackingResolvedSource<
  ChangeTrackingSource,
  ChangeTrackingSourceId
>[] =>
  [...new Set(sources)]
    .map((source) => ({
      source,
      sourceId: CHANGE_TRACKING_SOURCE_IDS[source],
    }))
    .sort((left, right) => left.sourceId - right.sourceId);

const buildChangeTrackingContext = (
  sources: readonly ChangeTrackingResolvedSource<
    ChangeTrackingSource,
    ChangeTrackingSourceId
  >[],
): ChangeTrackingActionContext | undefined => {
  if (sources.length === 0) return undefined;

  const value = sources.map(({ sourceId }) => sourceId).join(',');
  const headerBytes = new TextEncoder().encode(value).byteLength;

  if (headerBytes > CHANGE_TRACKING_HEADER_VALUE_LIMIT) {
    return undefined;
  }

  return {
    headerName: CHANGE_TRACKING_HEADER,
    headerValue: value,
  };
};

const trackChangeTrackingAction = (context: ChangeTrackingActionContext) => {
  try {
    datadogRum.addAction(CHANGE_TRACKING_DATADOG_ACTION, {
      [context.headerName]: context.headerValue,
    });
  } catch {
    // Change tracking should never block the business request.
  }
};

export const buildChangeTrackingHeaders = (
  changedSources: readonly ChangeTrackingSource[],
): ChangeTrackingHeaders | undefined => {
  const context = buildChangeTrackingContext(
    getChangeTrackingSources(changedSources),
  );
  if (!context) return undefined;

  trackChangeTrackingAction(context);

  return { [context.headerName]: context.headerValue };
};

export const trackChangeTrackingSources = (
  sources: readonly ChangeTrackingSource[],
) => {
  const context = buildChangeTrackingContext(getChangeTrackingSources(sources));
  if (!context) return;

  trackChangeTrackingAction(context);
};

export const buildChangeTrackingRequestConfig = (
  sources: readonly ChangeTrackingSource[],
): ChangeTrackingRequestConfig | undefined => {
  const headers = buildChangeTrackingHeaders(sources);
  return headers ? { headers } : undefined;
};
